import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, SaveIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { generatePath, useParams } from "react-router-dom";

import { Button, PageBreadcrumb, Spinner } from "@/modules/common/components";
import { AdminUserRole, AdminUserStatus } from "@/modules/common/types/Users.enum";
import { digitsOnly, formatValue } from "@/modules/common/utils/format";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import useToastOnError from "@/modules/common/hooks/useToastOnError";
import { ROUTES } from "@/routes/constants/routes";
import useUserDetails from "../user-details/hooks/useUserDetails";
import AdminUserFormFields from "../../components/AdminUserFormFields";
import useUpdateUser from "../../hooks/useUpdateUser";
import { AdminUserFormData, adminUserSchema } from "../../schemas";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import useRefreshSession from "@/modules/auth/hooks/useRefreshSession";

const UserEdit = () => {
  const { id } = useParams();
  const { canUserSee, session } = useSessionStore();
  const navigateBack = useNavigateBack(ROUTES.USERS.LIST);

  const { data: user, isLoading, isError, error } = useUserDetails({ id: id!, enabled: !!id });
  const updateUser = useUpdateUser();
  const refreshSession = useRefreshSession();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AdminUserFormData>({
    resolver: zodResolver(adminUserSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      role: "",
      status: "",
    },
  });

  useToastOnError(isError, error, "Erro ao carregar usuário.", navigateBack);

  useEffect(() => {
    if (!user) return;

    reset({
      name: user.name,
      email: user.email,
      phoneNumber: user.phoneNumber ? (formatValue("phoneNumber", user.phoneNumber) ?? "") : "",
      role: String(user.role),
      status: String(user.status),
    });
  }, [user, reset]);

  const onSubmit = async (values: AdminUserFormData) => {
    if (!id) return;

    await updateUser.mutateAsync({
      id,
      data: {
        name: values.name,
        email: values.email,
        phoneNumber: values.phoneNumber ? digitsOnly(values.phoneNumber) : undefined,
        role: Number(values.role) as AdminUserRole,
        status: Number(values.status) as AdminUserStatus,
      },
    });

    if (session?.user.id === id) {
      refreshSession.mutate();
    }
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb
        items={[
          { label: "Usuários Administrativos", to: ROUTES.USERS.LIST },
          ...(user && id ? [{ label: user.name, to: generatePath(ROUTES.USERS.DETAILS, { id }) }] : []),
          { label: "Editar" },
        ]}
      />

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : !!user ? (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col">
              <p className="text-[22px] font-bold text-ui-primary">Editar Usuário</p>
              <p className="text-[14px] text-ui-text-muted">Atualize os dados cadastrais de {user.name}.</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outlined"
                label="Voltar"
                icon={<ArrowLeft />}
                className="w-full sm:w-auto"
                onClick={navigateBack}
              />
              {canUserSee(AdminUserRole.ADMIN) && (
                <Button
                  type="submit"
                  label="Salvar Alterações"
                  icon={<SaveIcon />}
                  loading={updateUser.isPending}
                  disabled={updateUser.isPending}
                  className="w-full sm:w-auto"
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Dados Gerais</p>
              <p className="text-[12px] text-ui-text-muted">Informações cadastrais do usuário.</p>
            </div>

            <AdminUserFormFields register={register} control={control} errors={errors} />
          </div>
        </form>
      ) : null}
    </div>
  );
};

export default UserEdit;
